import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Globe, Search, Check, AlertCircle } from 'lucide-react';
import { 
  fetchAvailableLanguages, 
  fetchUserLanguage, 
  updateUserLanguage 
} from '../../../redux/settingsPages/language/languageActions';
import { clearLanguageError } from '../../../redux/settingsPages/language/languageSlice';

const LanguageSettingsPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { 
    available, 
    current, 
    loading, 
    updating, 
    error, 
    updateError 
  } = useSelector((state) => state.language);
  
  const user = useSelector((state) => state.profile?.user);
  
  const [selectedLanguage, setSelectedLanguage] = useState(current);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch available languages and current language on mount -- App.js
  // already fetches the current language once, app-wide, whenever the user
  // is authenticated, and `available` is only ever written by this effect,
  // so guarding both dispatches on it avoids redundantly re-fetching both
  // on every revisit of this settings page.
  useEffect(() => {
    if (available.length === 0) {
      dispatch(fetchAvailableLanguages());
      dispatch(fetchUserLanguage());
    }
  }, [dispatch, available.length]);

  // Update local state when Redux state changes
  useEffect(() => {
    setSelectedLanguage(current);
  }, [current]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return available;
    const q = searchQuery.toLowerCase();
    return available.filter(
      l => l.name.toLowerCase().includes(q) || l.nativeName.toLowerCase().includes(q)
    );
  }, [available, searchQuery]);

  const handleSaveChanges = async () => {
    if (selectedLanguage === current) {
      return;
    }

    try {
      await dispatch(updateUserLanguage(selectedLanguage)).unwrap();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save language:', err);
    }
  };

  const hasChanges = selectedLanguage !== current;
  const displayError = error || updateError;

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('language.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('language.subtitle')}
        </p>
      </div>

      <div className="max-w-6xl space-y-6">
        {/* User card */}
        {user && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-4">
              <img
                src={`https://ui-avatars.com/api/?name=${user.username}&background=c7d2fe&color=3730a3&bold=true`}
                alt="User Avatar"
                className="w-12 h-12 rounded-full"
              />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{user.username}</h2>
                <p className="text-gray-600 dark:text-gray-400">{user.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success message */}
        {showSuccess && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-600/30 rounded-lg p-4 flex items-center">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
            <p className="text-green-700 dark:text-green-400">{t('language.savedSuccess')}</p>
          </div>
        )}

        {/* Error message */}
        {displayError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600/30 rounded-lg p-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-700 dark:text-red-400">{displayError}</p>
            </div>
            <button
              onClick={() => dispatch(clearLanguageError())}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              ✕
            </button>
          </div>
        )}

        {/* Language selection */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-4 mb-4">
              <Globe className="w-6 h-6 text-blue-500" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('language.displayLanguage')}</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('language.description')}
            </p>

            <div className="relative mb-6">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('language.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : available.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No languages available. Please try again later.
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto pr-2 divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.map((lang) => (
                  <label
                    key={lang.code}
                    className="flex items-center justify-between py-3 px-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                  >
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{lang.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{lang.nativeName}</div>
                    </div>
                    <input
                      type="radio"
                      name="language"
                      className="hidden peer"
                      checked={selectedLanguage === lang.code}
                      onChange={() => setSelectedLanguage(lang.code)}
                      disabled={updating}
                    />
                    <span
                      className={`ml-4 inline-flex items-center justify-center w-6 h-6 rounded-full border transition-colors ${
                        selectedLanguage === lang.code
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-400 dark:border-gray-500'
                      }`}
                    >
                      {selectedLanguage === lang.code && <Check className="w-4 h-4 text-white" />}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {filtered.length === 0 && searchQuery && !loading && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No languages found matching "{searchQuery}"
              </div>
            )}
          </div>

          <div className="p-6 flex justify-end space-x-4">
            <button
              className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              onClick={() => setSelectedLanguage(current)}
              disabled={!hasChanges || updating}
            >
              {t('language.cancel')}
            </button>
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              onClick={handleSaveChanges}
              disabled={!hasChanges || updating}
            >
              {updating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('language.saving')}
                </>
              ) : (
                t('language.saveChanges')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageSettingsPage;